import { useState } from 'react';
import { supabase } from '../supabase';
import { ContentScoreService, ContentScoreResult, PlatformMetrics } from '../services/ContentScoreService';
import { streamOpenAIResponse } from '../openai';

interface AnalysisResult {
  score: ContentScoreResult;
  strengths: string[];
  weaknesses: string[];
  recommendations: string[];
  improvedVersions: string[];
  keywords: string[];
  platform: string;
}

export function useCaptionAnalysis() {
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const analyzeCaption = async (caption: string, platform: string) => {
    if (!caption.trim()) {
      setError('Please enter a caption to analyze');
      return null;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');
      
      // Calculate basic content score
      const scoreService = new ContentScoreService(user.id);      
      // Use enhanced analysis with account data if available
      let analysisResult;
      try {
        analysisResult = await scoreService.analyzeCaptionWithAccountData(caption, platform);
      } catch (error) {
        // If there's an error with account data, fall back to basic analysis
        console.error('Error with account data analysis, falling back to basic:', error);
        analysisResult = {
          score: await scoreService.analyzeCaption(caption, platform),
          accountMetrics: null
        };
      }
      
      const { score: scoreResult, accountMetrics } = analysisResult;
      
      console.log(`Analysis for ${platform} with account metrics:`, accountMetrics);
      
      // Build the prompt parts separately
      let promptParts = [
        `Analyze this ${platform} caption for SEO and engagement potential:

"${caption}"
`
      ];

      // If we have account metrics, add them to the prompt
      if (accountMetrics) {
        promptParts.push(`
Account metrics:
- Followers: ${accountMetrics.followers || accountMetrics.followers_count || 'Unknown'}
- Engagement rate: ${accountMetrics.engagement_rate || 'Unknown'}
- Posts: ${accountMetrics.tweet_count || accountMetrics.post_count || accountMetrics.video_count || 'Unknown'}
`);
      }

      promptParts.push(`
Format your response as JSON with the following structure:
{
  "strengths": ["Strength 1", "Strength 2"], // What's good about the caption
  "weaknesses": ["Weakness 1", "Weakness 2"], // What could be improved
  "improvedVersions": ["Improved version 1", "Improved version 2"], // 2-3 improved versions of the caption
  "keywords": ["keyword1", "keyword2"] // Important keywords detected or suggested
}

Be specific about searchability factors like keyword usage, length, engagement hooks, and platform-specific best practices.`);

      // Join all prompt parts
      const finalPrompt = promptParts.join('');

      let responseText = '';
      let aiAnalysis: any = {};

      await streamOpenAIResponse(
        [{ role: 'user', content: finalPrompt }],
        (chunk) => {
          responseText += chunk;
          try {
            // Clean the response text to handle markdown code blocks
            let cleanedResponse = responseText;
            if (cleanedResponse.includes('```json')) {
              cleanedResponse = cleanedResponse.replace(/```json\n|\n```/g, '');
            } else if (cleanedResponse.includes('```')) {
              cleanedResponse = cleanedResponse.replace(/```\n|\n```/g, '');
            }
            
            aiAnalysis = JSON.parse(cleanedResponse);
          } catch (e) {
            // Ignore parsing errors during streaming
          }
        },
        (error) => {
          throw error;
        }
      );

      // Combine our score with AI analysis
      const finalResult = {
        score: scoreResult, 
        strengths: aiAnalysis.strengths || ['Good use of keywords', 'Appropriate length for platform'],
        weaknesses: aiAnalysis.weaknesses || ['Could use more engagement hooks', 'Missing relevant hashtags'],
        recommendations: [...scoreResult.recommendations, ...(aiAnalysis.recommendations || [])],
        improvedVersions: aiAnalysis.improvedVersions || [caption],
        keywords: aiAnalysis.keywords || ['content', 'social media', platform],
        platform
      };

      setResult(finalResult);
      return analysisResult;
    } catch (err) {
      console.error('Error analyzing caption:', err);
      setError(err instanceof Error ? err.message : 'Failed to analyze caption');
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { result, loading, error, analyzeCaption };
}